import React, { useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Alert from '@glu/alerts-react';
import NavigationPanel from '@glu/steps-react';

import { ALERTVARIATIONS, WORKSPACES } from 'constants.js';
import useLocale from 'components/useLocale';
import Spinner from 'components/spinner/Spinner';

import {
  PAYMENT_TYPES,
  MAP_TYPES,
  PAYMENT_TYPE_NAMES,
  steps,
  ACTUAL_STEP,
  NESTED_POSITIVE_NEGATIVE_VALUE,
  ROOT_VALUE,
  MAPS,
  transformPaymentTypeToWire,
  transformPermissionsToPaymentTypes,
  getProcessMode,
  getHeaderLabel,
  onModifyMode,
  getStepperMoveOption,
  MOVE_ACTIONS,
  STEPPER_ERRORS
} from '../utils';
import CheckboxBlock from '../ImportTypeCheckbox/CheckboxBlock';
import Footer from './Footer';
import CreateWireImportMap from './CreateWireImportMap';
import useStyles from './CreateImportMaps.styles';
import CreateACHImportMapWrapper from '../CreateACHImportMap/CreateACHImportMapWrapper';
import ACHMapRouteLeaveNotification from '../ACHRouteLeaveNotificationGuard/ACHMapRouteLeaveNotificationGuard';

const CreateImportMapsComponent = ({
  achImportMapData,
  closeModal,
  importPermissions,
  mapCode,
  mapId,
  modalRootRef,
  modify
}) => {
  const classes = useStyles();
  const locale = useLocale();
  const [error, setError] = useState('');
  const [FormStepComponent, setFormStepComponent] = useState(null);
  const [sending, setSending] = useState(false);
  const [showLeavePopup, setLeavePopup] = useState(false);
  const [allowLeavingPage, setLeavingPage] = useState(false);
  const [lastLocation, setLastLocation] = useState(null);
  const [pendingStep, setPendingStep] = useState(null);
  const [wiresApiIsLoading, setLoadingForWiresApi] = useState(false); // This is only for WIRES, to understand when table api has loaded
  const [activeStep, setStep] = useState(1);
  const [paymentType, setPaymentType] = useState([]);
  const [mapTypes, setMapTypes] = useState([]);

  const isACHSelected = useMemo(() => paymentType.includes(PAYMENT_TYPE_NAMES.ACH), [paymentType]);

  useEffect(() => {
    // This hook is for MODIFY only
    if (!modify) {
      return;
    }

    onModifyMode(mapCode, setStep, setFormStepComponent);
  }, [mapCode, modify]);

  const onNext = () => {
    setError('');

    if (isACHSelected) {
      if (!mapTypes.length) {
        return setError(locale['wireImports.errorSelectMapType']);
      }
      if (mapTypes.length) {
        setStep(3);
        return setFormStepComponent(MAPS.ACH);
      }
    }

    setStep(3);

    return setFormStepComponent(MAPS.WIRE);
  };

  const onCloseAlert = useCallback(() => setError(''), []);

  const permissions = useMemo(
    () => transformPermissionsToPaymentTypes(importPermissions),
    [importPermissions]
  );

  const memoizedSteps = useMemo(
    () => steps(activeStep, FormStepComponent, permissions),
    [activeStep, FormStepComponent, permissions]
  );

  const stepperTransferExecutors = useMemo(
    () => ({
      [MOVE_ACTIONS.ONE_TWO]: () => {
        setStep(2);
        return setPaymentType([PAYMENT_TYPE_NAMES.ACH]);
      },
      [MOVE_ACTIONS.ONE_THREE]: (ERRORS) => {
        if (!paymentType.length) {
          return setError(ERRORS.NO_PAYMENT_TYPE_SELECTED);
        }
        return setStep(3);
      },
      [MOVE_ACTIONS.TWO_ONE]: () => {
        setStep(1);
        setPaymentType([]);
        setFormStepComponent(null);
        return setMapTypes([]);
      },
      [MOVE_ACTIONS.TWO_THREE]: (ERRORS) => {
        if (!mapTypes.length) {
          return setError(ERRORS.NO_MAP_FORMAT_SELECTED);
        }
        setStep(3);
        return setFormStepComponent(MAPS.ACH);
      },
      [MOVE_ACTIONS.THREE_ONE]: () => {
        setStep(1);
        setPaymentType([]);
        setFormStepComponent(null);
        return setMapTypes([]);
      },
      [MOVE_ACTIONS.THREE_TWO]: () => {
        if (isACHSelected) {
          setLeavingPage(false);
          setMapTypes([]);
          return setStep(2);
        }
        return null;
      }
    }),
    [setStep, setPaymentType, setMapTypes, isACHSelected, mapTypes, paymentType]
  );

  const onStepClickHandler = useCallback(
    ({ id }) => {
      const stepperMoveOption = getStepperMoveOption({ currentStep: activeStep, nextStep: id });
      const ERRORS = STEPPER_ERRORS(locale);

      setError(''); // Clear error before start

      switch (stepperMoveOption?.action) {
        case MOVE_ACTIONS.ONE_TWO: {
          return stepperTransferExecutors[MOVE_ACTIONS.ONE_TWO]();
        }

        case MOVE_ACTIONS.ONE_THREE: {
          return stepperTransferExecutors[MOVE_ACTIONS.ONE_THREE](ERRORS);
        }

        case MOVE_ACTIONS.TWO_ONE: {
          return stepperTransferExecutors[MOVE_ACTIONS.TWO_ONE]();
        }

        case MOVE_ACTIONS.TWO_THREE: {
          return stepperTransferExecutors[MOVE_ACTIONS.TWO_THREE](ERRORS);
        }

        case MOVE_ACTIONS.THREE_ONE: {
          if (!allowLeavingPage) {
            setLastLocation(null);
            return setPendingStep(MOVE_ACTIONS.THREE_ONE);
          }
          return stepperTransferExecutors[MOVE_ACTIONS.THREE_ONE]();
        }

        default:
        case MOVE_ACTIONS.THREE_TWO: {
          if (!allowLeavingPage && isACHSelected) {
            setLastLocation(null);
            return setPendingStep(MOVE_ACTIONS.THREE_TWO);
          }
          return stepperTransferExecutors[MOVE_ACTIONS.THREE_TWO]();
        }
      }
    },
    [setError, locale, activeStep, allowLeavingPage, isACHSelected, stepperTransferExecutors]
  );

  const onPaymentTypeCheckboxesChange = useCallback(
    (...args) => {
      setError('');
      setPaymentType(...args);
    },
    [setPaymentType, setError]
  );

  const header = useMemo(
    () => getHeaderLabel(achImportMapData, paymentType, modify, activeStep, locale),
    [achImportMapData, paymentType, modify, activeStep, locale]
  );
  const wireType = useMemo(() => transformPaymentTypeToWire(paymentType), [paymentType]);

  const paymentTypesData = useMemo(
    () => PAYMENT_TYPES({ locale, setFormStepComponent, setStep }),
    [locale]
  );
  const mapTypesData = useMemo(() => MAP_TYPES(locale), [locale]);
  const processMode = useMemo(
    () => achImportMapData?.achImportMapHeader?.processMode || getProcessMode(mapTypes),
    [mapTypes, achImportMapData]
  );
  const FormComponent = useMemo(
    () => (FormStepComponent === MAPS.ACH ? CreateACHImportMapWrapper : CreateWireImportMap),
    [FormStepComponent]
  );

  const onCloseModal = useCallback(
    (canLeave) => {
      if (
        allowLeavingPage ||
        canLeave ||
        (activeStep === 1 && !permissions.includes('ACH')) ||
        (activeStep === 1 && !paymentType.length)
      ) {
        setLeavePopup(false);
        return closeModal();
      }

      if (activeStep === 3) {
        setLastLocation({ pathname: WORKSPACES.PYMTCNTR });
      }

      return setLeavePopup(true);
    },
    [
      setLeavePopup,
      allowLeavingPage,
      closeModal,
      activeStep,
      setLastLocation,
      permissions,
      paymentType
    ]
  );

  return (
    <ACHMapRouteLeaveNotification
      modify={modify}
      allowLeavingPage={allowLeavingPage}
      setLeavingPage={setLeavingPage}
      setPendingStep={setPendingStep}
      pendingStep={pendingStep}
      stepperTransferExecutors={stepperTransferExecutors}
      setLeavePopup={setLeavePopup}
      showLeavePopup={showLeavePopup}
      closeModal={closeModal}
      paymentType={paymentType}
      lastLocation={lastLocation}
      setLastLocation={setLastLocation}
      setSending={setSending}
    >
      {wiresApiIsLoading ? <Spinner /> : null} {/* FOR WIRES only */}
      <div className={classnames(classes.wrapper, { [classes.hidden]: wiresApiIsLoading })}>
        {' '}
        {/* hidden - is for WIRES only */}
        <h2 className={classes.header}>{header}</h2>
        {!modify && (
          <NavigationPanel
            className={classes.navigation}
            idField="create-import-maps-steps"
            data={memoizedSteps}
            currentStep={ACTUAL_STEP}
            rootValue={ROOT_VALUE}
            positiveNegativeValue={NESTED_POSITIVE_NEGATIVE_VALUE}
            DataDetails={({ record: { name } }) => <div data-qa={`step-${name}`}>{name}</div>}
            onStepClick={onStepClickHandler}
            numberIdentifier={false}
            withIdentifier={false}
            actionable
          />
        )}
        {error ? (
          <div className="col-12">
            <Alert variant={ALERTVARIATIONS.ERROR} onHide={onCloseAlert} title={error} />
          </div>
        ) : null}
        {activeStep === 3 ? (
          <FormComponent
            setLeavingPage={setLeavingPage}
            // props for WIRES only
            mapId={mapId}
            wireType={wireType}
            setLoadingForWiresApi={setLoadingForWiresApi}
            setPaymentType={setPaymentType}
            //
            setError={setError}
            header={header}
            sending={sending}
            setSending={setSending}
            modalRootRef={modalRootRef}
            mapCode={mapCode}
            closeModal={onCloseModal}
            modify={modify}
            achImportMapData={achImportMapData}
            processMode={processMode}
          />
        ) : (
          <>
            {!modify && (
              <>
                <CheckboxBlock
                  highlightBorder
                  disableCheckboxes
                  disableMultiselect
                  header={locale['wireImports.selectPaymentType']}
                  checkboxDataStructure={paymentTypesData}
                  selectedData={paymentType}
                  onChange={onPaymentTypeCheckboxesChange}
                  permissions={permissions}
                />

                {isACHSelected ? (
                  <CheckboxBlock
                    highlightBorder
                    header={locale['wireImports.selectMapType']}
                    subHeader={locale['wireImports.chooseAllThatApply']}
                    checkboxDataStructure={mapTypesData}
                    selectedData={mapTypes}
                    onChange={setMapTypes}
                  />
                ) : null}
              </>
            )}
            {!modify && (
              <Footer
                activeStep={activeStep}
                onCancel={() => onCloseModal(paymentType.length)}
                {...(paymentType.length ? { onNext } : {})}
              />
            )}
          </>
        )}
      </div>
    </ACHMapRouteLeaveNotification>
  );
};

CreateImportMapsComponent.propTypes = {
  importPermissions: PropTypes.arrayOf(PropTypes.shape({})),
  achImportMapData: PropTypes.arrayOf(PropTypes.shape({})),
  modify: PropTypes.bool,
  closeModal: PropTypes.func,
  mapId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  mapCode: PropTypes.oneOfType([PropTypes.string]),
  modalRootRef: PropTypes.node.isRequired
};

CreateImportMapsComponent.defaultProps = {
  importPermissions: [],
  achImportMapData: null,
  mapCode: null,
  modify: false,
  closeModal() {}
};

export default CreateImportMapsComponent;
